/** The "everything this record touches" row under a detail-page header.
 *
 *  A popover would hide these behind a hover on some value that happens to
 *  name the record; the whole point is that a user's bookings, orders,
 *  delivery logs and sessions are the FIRST thing an operator reaches for, so
 *  they are chips you can see. Each chip is a plain anchor into the target
 *  grid, opened in a new tab like every other filter link (D1) — plain `<a>`
 *  rather than a RouterLink so the scope-aware anchor patch carries `?scope=`.
 *
 *  Takes the `act.filter(...)` actions a `<family>Links` module builds, so the
 *  hrefs are the contract-tested ones and this file holds no URL knowledge. */
import { Box, Chip, Typography } from '@mui/material';

import type { CellAction } from '@/components/DataGrid/CellActionsPopover';

export function RelatedRecordsStrip({
  actions,
  label = 'Related records',
}: {
  actions: CellAction[];
  label?: string;
}) {
  const links = actions.filter((a): a is CellAction & { href: string } => Boolean(a.href));
  if (links.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.75,
        mb: 2,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        {label}:
      </Typography>
      {links.map((a) => (
        <Chip
          key={a.key}
          size="small"
          variant="outlined"
          clickable
          component="a"
          href={a.href}
          target="_blank"
          rel="noopener noreferrer"
          label={a.label}
          sx={{ height: 22, fontSize: 12 }}
        />
      ))}
    </Box>
  );
}
